import {
  Partners,
  QUOTE_TYPES,
  QUOTE_WORKFLOW_STATUSES,
  QuoteType,
  QuoteWorkflowStatus,
} from '@lockton-apps/shared/interfaces';
import { createDefinition } from '@lockton-apps/shared/util/helpers';
import { WorkflowConfigOverride, WorkflowStatusMeta } from './interface';

const COMMON_VARIANTS = {
  [QUOTE_TYPES.Renewal + Partners.LSOS_ADDON]: {
    base: {
      label: $localize`Proposal received`,
    },
  },
};

export const getProposalWorkflowMeta2 = createDefinition<WorkflowStatusMeta>({
  base: {
    label: $localize`Proposal complete`,
    icon: 'application_complete_thin',
    level: 'info',
  },
  variants: {
    [QUOTE_WORKFLOW_STATUSES.NewProposal]: {
      base: {
        label: $localize`New proposal`,
        icon: 'application_new_thin',
        level: 'info',
      },
    },
    [QUOTE_WORKFLOW_STATUSES.ProposalSent]: {
      base: {
        label: $localize`Proposal sent`,
        icon: 'application_sent_thin',
        level: 'info',
      },
    },
    [QUOTE_WORKFLOW_STATUSES.ProposalInProgress]: {
      base: {
        label: $localize`Proposal in progress`,
        icon: 'application_progress_thin',
        level: 'info',
      },
    },
    [QUOTE_WORKFLOW_STATUSES.ProposalComplete]: {
      base: {
        label: $localize`Proposal complete`,
        icon: 'application_complete_thin',
        level: 'info',
      },
      variants: {
        [QUOTE_TYPES.Renewal]: {
          base: {},
          variants: {
            [Partners.LSOS]: {
              base: {
                label: $localize`Proposal received`,
              },
            },
            [Partners.LSOS_ADDON]:
              COMMON_VARIANTS[QUOTE_TYPES.Renewal + Partners.LSOS_ADDON],
          },
        },
      },
    },
    [QUOTE_WORKFLOW_STATUSES.ReviewRequired]: {
      base: {
        label: $localize`Review required`,
        icon: 'under_review_thin',
        level: 'info',
      },
    },
    [QUOTE_WORKFLOW_STATUSES.ProposalReleased]: {
      base: {
        label: $localize`Proposal returned`,
        icon: 'application_released_thin',
        level: 'success',
      },
    },
    [QUOTE_WORKFLOW_STATUSES.QuoteRequested]: {
      base: {},
      variants: {
        [QUOTE_TYPES.Renewal]: {
          base: {},
          variants: {
            [Partners.LSOS_ADDON]:
              COMMON_VARIANTS[QUOTE_TYPES.Renewal + Partners.LSOS_ADDON],
          },
        },
      },
    },
    [QUOTE_WORKFLOW_STATUSES.QuoteReady]: {
      base: {},
      variants: {
        [QUOTE_TYPES.Renewal]: {
          base: {},
          variants: {
            [Partners.LSOS_ADDON]:
              COMMON_VARIANTS[QUOTE_TYPES.Renewal + Partners.LSOS_ADDON],
          },
        },
      },
    },
    [QUOTE_WORKFLOW_STATUSES.QuoteSent]: {
      base: {},
      variants: {
        [QUOTE_TYPES.Renewal]: {
          base: {},
          variants: {
            [Partners.LSOS_ADDON]:
              COMMON_VARIANTS[QUOTE_TYPES.Renewal + Partners.LSOS_ADDON],
          },
        },
      },
    },
    [QUOTE_WORKFLOW_STATUSES.QuoteAccepted]: {
      base: {},
      variants: {
        [QUOTE_TYPES.Renewal]: {
          base: {},
          variants: {
            [Partners.LSOS_ADDON]:
              COMMON_VARIANTS[QUOTE_TYPES.Renewal + Partners.LSOS_ADDON],
          },
        },
      },
    },
    [QUOTE_WORKFLOW_STATUSES.AwaitingPayment]: {
      base: {},
      variants: {
        [QUOTE_TYPES.Renewal]: {
          base: {},
          variants: {
            [Partners.LSOS_ADDON]:
              COMMON_VARIANTS[QUOTE_TYPES.Renewal + Partners.LSOS_ADDON],
          },
        },
      },
    },
    [QUOTE_WORKFLOW_STATUSES.PolicyDocumentationSent]: {
      base: {},
      variants: {
        [QUOTE_TYPES.Renewal]: {
          base: {},
          variants: {
            [Partners.LSOS_ADDON]:
              COMMON_VARIANTS[QUOTE_TYPES.Renewal + Partners.LSOS_ADDON],
          },
        },
      },
    },
    [QUOTE_WORKFLOW_STATUSES.NotTakenUp]: {
      base: {},
      variants: {
        [QUOTE_TYPES.Renewal]: {
          base: {},
          variants: {
            [Partners.LSOS_ADDON]: {
              base: {
                label: $localize`Not taken up`,
                icon: 'renewal_ntu_thin',
                level: 'error',
              },
            },
          },
        },
      },
    },
  },
});

export const partnerProposalWorkflowStatusOverrides: Partial<
  Record<Partners | 'DEFAULT', WorkflowConfigOverride>
> = {
  DEFAULT: {},
};

export function getProposalWorkflowMeta(
  status: QuoteWorkflowStatus,
  quoteType: QuoteType,
  partner: Partners | 'DEFAULT' = 'DEFAULT'
) {
  return getProposalWorkflowMeta2([status, quoteType, partner]);
}
